import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Mail, Phone, MapPin, MessageSquare, ChevronRight, X, Bot, Upload, Send, AlertTriangle, CheckCircle2, Download } from "lucide-react";
import { API } from "@/lib/api";
import { getToken } from "@/lib/auth";

const STATUS_ORDER = [
  "prospected", "emailed", "whatsapped", "replied", "interested",
  "signed_up", "not_interested", "do_not_contact",
];

const STATUS_COLOR = {
  prospected: "bg-neutral-50 text-neutral-600 border-neutral-200",
  emailed: "bg-blue-50 text-blue-700 border-blue-200",
  whatsapped: "bg-green-50 text-green-700 border-green-200",
  replied: "bg-amber-50 text-amber-700 border-amber-200",
  interested: "bg-emerald-50 text-emerald-700 border-emerald-200",
  signed_up: "bg-[#FF5100]/10 text-[#FF5100] border-[#FF5100]/30",
  not_interested: "bg-neutral-50 text-neutral-400 border-neutral-200",
  do_not_contact: "bg-red-50 text-red-700 border-red-200",
};

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "short" }) : "—";

/** SA WhatsApp heuristic — mobile numbers start with 06/07/08 (or 27 6/7/8) */
const hasWhatsappNumber = (phone) => {
  if (!phone) return false;
  const digits = phone.replace(/[^\d]/g, "");
  return /^(27[678]|0[678])/.test(digits);
};

export default function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [template, setTemplate] = useState("supplier_outreach");
  const [status, setStatus] = useState(null); // {enabled, sent_today, daily_cap, remaining_today, ...}
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = filter ? { status: filter } : {};
      const r = await axios.get(`${API}/torque/leads`, { ...auth(), params });
      setLeads(r.data);
    } catch {
      toast.error("Could not load leads");
    } finally { setBusy(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/leads/bulk-wa/status`, auth());
      setStatus(r.data);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Eligibility check — must match backend rules + WA-capable phone
  const isEligible = (l) => (
    hasWhatsappNumber(l.phone) &&
    l.status !== "do_not_contact" &&
    l.status !== "signed_up" &&
    !l.do_not_contact &&
    (!l.last_wa_at || (Date.now() - new Date(l.last_wa_at).getTime()) > 14 * 86400000)
  );

  const eligibleLeads = useMemo(() => leads.filter(isEligible), [leads]);
  const selectedEligible = useMemo(
    () => Array.from(selectedIds).filter(id => eligibleLeads.some(l => l.id === id)),
    [selectedIds, eligibleLeads]
  );
  const allEligibleSelected = eligibleLeads.length > 0 && eligibleLeads.every(l => selectedIds.has(l.id));

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (allEligibleSelected) return new Set();
      const s = new Set(prev);
      eligibleLeads.forEach(l => s.add(l.id));
      return s;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/leads/import`, fd, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const d = r.data;
      const parts = [`${d.imported} new`, `${d.merged} merged`];
      if (d.skipped_existing_supplier) parts.push(`${d.skipped_existing_supplier} skipped (already supplier)`);
      if (d.skipped_no_name) parts.push(`${d.skipped_no_name} skipped (no name)`);
      if (d.error_count) parts.push(`${d.error_count} errors`);
      toast.success(`Import done: ${parts.join(" · ")}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImportBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const bulkSendWA = async () => {
    const toSend = leads.filter(l => selectedIds.has(l.id) && isEligible(l));
    if (toSend.length === 0) {
      toast.error("No eligible leads selected. Tick some boxes first.");
      return;
    }
    if (status && !status.enabled) {
      toast.error("Bulk WA is disabled. Set WA_BULK_ENABLED=true in env and redeploy.");
      return;
    }
    if (status && toSend.length > status.remaining_today) {
      toast.error(`Only ${status.remaining_today} sends left in today's cap. Reduce your selection.`);
      return;
    }
    const templateName = { supplier_outreach: "spannr_supplier_outreach",
                            trial_reminder: "spannr_trial_reminder",
                            search_resume: "spannr_search_resume" }[template];
    const cost = (toSend.length * 0.4).toFixed(2);
    if (!window.confirm(
      `Send "${templateName}" WhatsApp template to ${toSend.length} leads?\n\n` +
      `Approximate cost: R${cost} (R0.40 per message · marketing tier)\n` +
      `Rate: 1 msg every ${Math.max(3, status?.min_rate_seconds || 3)}s (~${Math.ceil((toSend.length * (status?.min_rate_seconds || 3))/60)} min total)\n` +
      `Meta daily cap: ${status?.sent_today || 0} / ${status?.daily_cap || 200} used today\n\n` +
      `Continue?`
    )) return;

    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/admin/leads/bulk-wa`, {
        lead_ids: toSend.map(l => l.id),
        template,
        rate_limit_seconds: status?.min_rate_seconds || 3.0,
        confirm: true,
      }, auth());
      const d = r.data;
      toast.success(
        `WA sent: ${d.sent} · skipped: ${d.skipped_no_phone + d.skipped_recent + d.skipped_dnc} · failed: ${d.failed}`,
        { duration: 8000 }
      );
      clearSelection();
      loadStatus();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk send failed");
    } finally {
      setBulkBusy(false);
    }
  };

  // Counts by status
  const counts = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = leads.filter(l => l.status === s).length;
    return acc;
  }, {});

  return (
    <div data-testid="admin-leads">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-mono-tech text-[#FF5100]">
            <Bot size={12} /> TORQUE · WINGMAN PIPELINE
          </div>
          <div className="text-[11px] text-neutral-500 mt-1">
            Import Torque&apos;s Google Sheet (CSV) and fire WhatsApp invites in bulk.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={importCsv}
            className="hidden"
            data-testid="leads-import-file"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={importBusy}
            data-testid="leads-import-btn"
            className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 px-3 h-9 border border-neutral-200 hover:border-[#FF5100] hover:text-[#FF5100] transition-colors"
          >
            <Upload size={12} /> {importBusy ? "Importing..." : "Import CSV"}
          </button>
          <a
            href="/api/admin/leads/template.xlsx"
            data-testid="leads-template-xlsx"
            className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 px-3 h-9 border border-neutral-200 hover:border-[#FF5100] hover:text-[#FF5100] transition-colors"
            title="Download Excel template — open in Google Sheets, fill in, export as CSV, then Import"
          >
            <Download size={12} /> Excel template
          </a>
          <a
            href="/api/admin/leads/template.csv"
            data-testid="leads-template-csv"
            className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 px-3 h-9 border border-neutral-200 hover:border-neutral-300 transition-colors text-neutral-600"
            title="Download bare CSV template"
          >
            <Download size={12} /> CSV
          </a>
          <select
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            data-testid="leads-bulk-wa-template"
            className="text-[11px] font-bold uppercase tracking-wider px-2 h-9 border border-neutral-200 bg-white cursor-pointer hover:border-[#FF5100]"
          >
            {(status?.templates || [
              { key: "supplier_outreach", name: "spannr_supplier_outreach", sid_set: true },
              { key: "trial_reminder", name: "spannr_trial_reminder", sid_set: true },
              { key: "search_resume", name: "spannr_search_resume", sid_set: true },
            ]).map((t) => (
              <option key={t.key} value={t.key} disabled={!t.sid_set}>
                {t.name}{!t.sid_set ? " (no SID)" : ""}
              </option>
            ))}
          </select>
          <button
            onClick={bulkSendWA}
            disabled={bulkBusy || selectedEligible.length === 0}
            data-testid="leads-bulk-wa-btn"
            className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 px-3 h-9 bg-[#FF5100] hover:bg-[#E64A00] disabled:opacity-40 text-white transition-colors"
          >
            <Send size={12} /> {bulkBusy ? "Sending..." : `Send to ${selectedEligible.length} selected`}
          </button>
          <button onClick={() => { load(); loadStatus(); }} disabled={busy} className="text-xs flex items-center gap-1.5 text-neutral-500 hover:text-[#FF5100]">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Quota bar */}
      {status && (
        <div className={`mb-4 border ${
          !status.enabled
            ? "bg-red-50 border-red-200 text-red-800"
            : status.remaining_today <= 20
            ? "bg-amber-50 border-amber-200 text-amber-800"
            : "bg-emerald-50 border-emerald-200 text-emerald-800"
        }`}>
          {!status.enabled ? (
            <div className="p-3 space-y-2" data-testid="bulk-wa-disabled-banner">
              <div className="flex items-center gap-2 text-[12px]">
                <AlertTriangle size={14} />
                <span className="font-bold uppercase tracking-wider">Bulk WhatsApp is disabled</span>
              </div>
              <div className="text-[11px] leading-relaxed">
                <span>This is a safety lock. To enable bulk sends:</span>
                <ol className="list-decimal ml-5 mt-1.5 space-y-0.5">
                  <li>Open your <strong>Emergent dashboard</strong> → this app</li>
                  <li>Find the <strong>Environment Variables</strong> panel</li>
                  <li>Add: <code className="font-mono bg-white/70 px-1.5 py-0.5 border border-red-200">WA_BULK_ENABLED</code> = <code className="font-mono bg-white/70 px-1.5 py-0.5 border border-red-200">true</code></li>
                  <li>Save &amp; redeploy — this banner turns green when it&apos;s active</li>
                </ol>
              </div>
            </div>
          ) : (
            <div className="p-3 text-[11px] flex items-center gap-3 flex-wrap">
              <span className="font-bold uppercase tracking-wider">Meta daily cap:</span>
              <span><span className="font-mono">{status.sent_today}</span> / <span className="font-mono">{status.daily_cap}</span> sent today</span>
              <span>·</span>
              <span><span className="font-mono">{status.remaining_today}</span> remaining</span>
              <span>·</span>
              <span>Max <span className="font-mono">{status.max_per_call}</span> per batch</span>
              <span>·</span>
              <span>{status.min_rate_seconds}s between sends</span>
            </div>
          )}
        </div>
      )}

      {/* Status pills */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        <button
          onClick={() => setFilter("")}
          className={`text-[11px] uppercase tracking-wider font-bold px-2.5 h-8 border ${
            !filter ? "bg-neutral-900 border-neutral-900 text-white" : "border-neutral-200 hover:border-neutral-900"
          }`}
        >
          All ({leads.length})
        </button>
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`leads-filter-${s}`}
            className={`text-[11px] uppercase tracking-wider font-bold px-2.5 h-8 border ${
              filter === s ? "bg-[#FF5100] border-[#FF5100] text-white" : "border-neutral-200 hover:border-neutral-900"
            }`}
          >
            {s.replace("_", " ")} ({counts[s] || 0})
          </button>
        ))}
      </div>

      <div className="border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-3 py-2.5 w-8">
                <input
                  type="checkbox"
                  checked={allEligibleSelected}
                  onChange={toggleSelectAll}
                  disabled={eligibleLeads.length === 0}
                  data-testid="leads-select-all"
                  title={`Select all ${eligibleLeads.length} eligible leads`}
                  className="w-4 h-4 accent-[#FF5100] cursor-pointer"
                />
              </th>
              <th className="text-left px-3 py-2.5 font-mono-tech">Business</th>
              <th className="text-left px-3 py-2.5 font-mono-tech hidden md:table-cell">City</th>
              <th className="text-left px-3 py-2.5 font-mono-tech hidden md:table-cell">Contact</th>
              <th className="text-center px-2 py-2.5 font-mono-tech" title="Has a WhatsApp-capable mobile number">WA</th>
              <th className="text-left px-3 py-2.5 font-mono-tech hidden lg:table-cell">Source</th>
              <th className="text-left px-3 py-2.5 font-mono-tech">Status</th>
              <th className="text-left px-3 py-2.5 font-mono-tech hidden sm:table-cell">Last activity</th>
              <th className="text-right px-3 py-2.5 font-mono-tech"></th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => {
              const eligible = isEligible(l);
              const checked = selectedIds.has(l.id);
              return (
              <tr
                key={l.id}
                data-testid={`lead-row-${l.id}`}
                className={`border-t border-neutral-100 hover:bg-neutral-50 ${checked ? "bg-[#FFF6F0]" : ""}`}
              >
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelect(l.id)}
                    disabled={!eligible}
                    data-testid={`lead-select-${l.id}`}
                    title={eligible ? "Include in bulk send" : "Not eligible (no phone / signed up / DNC / recent WA)"}
                    className="w-4 h-4 accent-[#FF5100] cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  />
                </td>
                <td className="px-3 py-2.5 cursor-pointer" onClick={() => setSelected(l)}>
                  <div className="font-medium">{l.business_name}</div>
                  <div className="text-[11px] text-neutral-500 md:hidden">{l.city}</div>
                </td>
                <td className="px-3 py-2.5 hidden md:table-cell cursor-pointer" onClick={() => setSelected(l)}>{l.city || "—"}</td>
                <td className="px-3 py-2.5 hidden md:table-cell text-neutral-600 cursor-pointer" onClick={() => setSelected(l)}>
                  {l.email && <div className="text-xs truncate max-w-[180px]">{l.email}</div>}
                  {l.phone && <div className="text-[11px] text-neutral-500">{l.phone}</div>}
                </td>
                <td className="px-2 py-2.5 text-center cursor-pointer" onClick={() => setSelected(l)}>
                  {hasWhatsappNumber(l.phone) ? (
                    <CheckCircle2 size={16} className="inline text-green-600" title="WhatsApp-capable" />
                  ) : (
                    <span className="text-neutral-300 text-lg" title={l.phone ? "Landline / not WA-capable" : "No phone"}>—</span>
                  )}
                </td>
                <td className="px-3 py-2.5 hidden lg:table-cell text-xs text-neutral-500 cursor-pointer" onClick={() => setSelected(l)}>{l.source || "—"}</td>
                <td className="px-3 py-2.5 cursor-pointer" onClick={() => setSelected(l)}>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 border ${STATUS_COLOR[l.status] || ""}`}>
                    {(l.status || "").replace("_", " ")}
                  </span>
                </td>
                <td className="px-3 py-2.5 hidden sm:table-cell text-xs text-neutral-500 cursor-pointer" onClick={() => setSelected(l)}>
                  {l.last_wa_at ? `WA ${fmtDate(l.last_wa_at)}` :
                   l.last_email_at ? `Email ${fmtDate(l.last_email_at)}` :
                   fmtDate(l.created_at)}
                </td>
                <td className="px-3 py-2.5 text-right text-neutral-400 cursor-pointer" onClick={() => setSelected(l)}>
                  <ChevronRight size={14} />
                </td>
              </tr>
              );
            })}
            {leads.length === 0 && !busy && (
              <tr><td colSpan={9} className="px-3 py-12 text-center text-neutral-500">
                {filter ? `No leads in "${filter}" status.` : "No leads yet. Torque will populate this once he starts outreach."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <LeadDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onChanged={(updated) => {
            setLeads(ls => ls.map(l => l.id === updated.id ? updated : l));
            setSelected(updated);
          }}
        />
      )}
    </div>
  );
}

function LeadDrawer({ lead, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);

  const update = async (patch) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/torque/leads/${lead.id}`, patch, auth());
      onChanged(r.data);
      toast.success("Updated");
    } catch {
      toast.error("Update failed");
    } finally { setBusy(false); }
  };

  const sendWA = async () => {
    if (!lead.phone) { toast.error("No phone number on file"); return; }
    if (!window.confirm(`Send WhatsApp template "spannr_supplier_outreach" to ${lead.business_name} (${lead.phone})?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/torque/wa-invite`, {
        lead_id: lead.id, template: "supplier_outreach",
      }, auth());
      if (r.data.ok) {
        toast.success("WhatsApp template sent");
        update({ status: "emailed" }); // bump status (or you may want a dedicated wa_sent status later)
      } else {
        toast.error(r.data.error || "Send failed");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="lead-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white border-l border-neutral-200 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-5 py-3 flex items-center justify-between">
          <div className="text-xs font-mono-tech tracking-[0.2em] text-[#FF5100]">LEAD · DETAIL</div>
          <button onClick={onClose} className="p-1.5 text-neutral-500 hover:text-neutral-900"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <h2 className="font-display text-2xl font-extrabold leading-tight">{lead.business_name}</h2>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 border ${STATUS_COLOR[lead.status] || ""}`}>
                {(lead.status || "").replace("_", " ")}
              </span>
              {lead.supplier_type && (
                <span className="text-[11px] capitalize text-neutral-500">{lead.supplier_type}</span>
              )}
            </div>
          </div>

          {/* Status changer */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1.5">Change status</div>
            <div className="flex flex-wrap gap-1">
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  onClick={() => update({ status: s })}
                  disabled={busy || s === lead.status}
                  className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 border transition-colors ${
                    s === lead.status ? "bg-neutral-900 border-neutral-900 text-white" : "border-neutral-200 hover:border-neutral-900"
                  }`}
                >
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>

          {/* Contact info */}
          <div className="space-y-2 text-sm">
            <Row icon={Mail} label="Email" value={lead.email} />
            <Row icon={Phone} label="Phone" value={lead.phone} />
            <Row icon={MapPin} label="City" value={lead.city} />
          </div>

          {/* Activity log */}
          <div className="border-t border-neutral-100 pt-4 space-y-1.5 text-xs text-neutral-600">
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1">Activity</div>
            <div>Created — {fmtDate(lead.created_at)}{lead.source ? ` · via ${lead.source}` : ""}</div>
            {lead.last_email_at && (
              <div>Last email — {fmtDate(lead.last_email_at)}{lead.last_email_subject ? `: "${lead.last_email_subject}"` : ""}</div>
            )}
            {lead.last_wa_at && (
              <div>Last WhatsApp — {fmtDate(lead.last_wa_at)} · template <span className="font-mono-tech">{lead.last_wa_template}</span> · {lead.last_wa_status}{lead.last_wa_error ? ` (${lead.last_wa_error})` : ""}</div>
            )}
          </div>

          {/* Notes */}
          {lead.notes && (
            <div className="border-t border-neutral-100 pt-4">
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1">Notes</div>
              <pre className="text-xs whitespace-pre-wrap font-sans text-neutral-700">{lead.notes}</pre>
            </div>
          )}

          {/* Actions */}
          <div className="border-t border-neutral-100 pt-4 flex flex-wrap gap-2">
            <button
              onClick={sendWA}
              disabled={busy || !lead.phone}
              data-testid="lead-send-wa"
              className="px-4 h-10 bg-[#FF5100] hover:bg-[#E64A00] disabled:opacity-40 text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
            >
              <MessageSquare size={12} /> Send WhatsApp invite
            </button>
            {lead.email && (
              <a
                href={`mailto:${lead.email}`}
                className="px-4 h-10 border border-neutral-200 hover:border-neutral-900 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
              >
                <Mail size={12} /> Email
              </a>
            )}
          </div>
          {!lead.phone && (
            <div className="text-[11px] text-neutral-500">No phone on file — add one before sending WhatsApp.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="mt-1 text-neutral-400 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">{label}</div>
        <div className="font-medium truncate">{value}</div>
      </div>
    </div>
  );
}
